import { Languages } from '../LanguageContext';
import { LANGUAGE_ITEM_NAME } from '../consts/consts';

/** Mirrors psifasbeetleutils.languages (normalize_lang, LANG_ALIAS_MAP, DEFAULT_LANG). */
export const DEFAULT_LANG = 'iw';

const LANG_ALIAS_MAP: Record<string, string> = {
  he: DEFAULT_LANG,
  heb: DEFAULT_LANG,
  en: 'en-US',
  eng: 'en-US',
  ara: 'ar',
  rus: 'ru',
};

const SUPPORTED_LANG_CODES = new Set([DEFAULT_LANG, 'en-US', 'ar', 'ru']);

const LANG_CODE_TO_ENUM: Record<string, Languages> = {
  [DEFAULT_LANG]: Languages.HEBREW,
  'en-US': Languages.ENGLISH,
  ar: Languages.ARABIC,
  ru: Languages.RUSSIAN,
};

const ENUM_TO_LANG_CODE = Object.fromEntries(
  Object.entries(LANG_CODE_TO_ENUM).map(([code, lang]) => [lang, code]),
) as Record<Languages, string>;

/** Same rules as psifasbeetleutils.languages.normalize_lang; null when empty/unknown (URL parsing). */
export function normalizeLangCode(raw: string | null | undefined): string | null {
  if (!raw || !raw.trim()) {
    return null;
  }

  const cleaned = raw.trim().toLowerCase();
  if (SUPPORTED_LANG_CODES.has(cleaned)) {
    return cleaned;
  }

  const mapped = LANG_ALIAS_MAP[cleaned];
  if (mapped) {
    return mapped;
  }

  if (cleaned.startsWith('en')) {
    return 'en-US';
  }

  return null;
}

export function parseLangFromUrl(search: string): Languages | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const normalized = normalizeLangCode(params.get('lang'));
  if (!normalized) {
    return null;
  }
  return LANG_CODE_TO_ENUM[normalized] ?? null;
}

export function languageStorageKey(uuid: string): string {
  return `${LANGUAGE_ITEM_NAME}:${uuid}`;
}

export function readStoredLanguage(uuid?: string): Languages | null {
  if (!uuid) {
    return null;
  }
  return sessionStorage.getItem(languageStorageKey(uuid)) as Languages | null;
}

export function resolveInitialLanguage(search: string, uuid?: string): Languages {
  return parseLangFromUrl(search) ?? readStoredLanguage(uuid) ?? Languages.HEBREW;
}

export function appendLangForLanguage(path: string, lang: Languages): string {
  const code = ENUM_TO_LANG_CODE[lang];
  return code === DEFAULT_LANG ? path : `${path}?lang=${encodeURIComponent(code)}`;
}

/** Map cache/API BCP-47 code to UI Languages enum; defaults to Hebrew. */
export function languageFromCode(code: string | null | undefined): Languages {
  const normalized = normalizeLangCode(code);
  if (!normalized) {
    return Languages.HEBREW;
  }
  return LANG_CODE_TO_ENUM[normalized] ?? Languages.HEBREW;
}
